import React from 'react'
import { Route, useNavigate, Navigate } from 'react-router-dom'
import { Routes } from '@datadog/browser-rum-react/react-router-v7'
import { Security, LoginCallback } from '@okta/okta-react'
import { toRelativeUrl } from '@okta/okta-auth-js'

import { ThemeProvider } from '@/components/shared/theme-provider'
import { AlfredTour } from '@/components/alfred/alfred-tour'
import { AlfredBulkImportJobProvider } from '@/components/alfred/bulk-import/alfred-bulk-import-job-provider'
import { Toaster } from '@/components/ui/toaster'
import { oktaAuth } from '@/auth/oktaConfig'
import { SecureRoute } from '@/auth/SecureRoute'
import { DatadogUserIdentity } from '@/monitoring/datadog-user-identity'
import SingleSignOn from '@/auth/SingleSignOn'
import { LoginPage } from '@/pages/login'
import { GapDevicesPage } from '@/pages/gap-devices'
import { GapClientsPage } from '@/pages/gap-clients'
import { AlfredGlobalPage } from '@/pages/alfred-global'
import { AlfredClientsPage } from '@/pages/alfred-clients'
import { AlfredDevicesPage } from '@/pages/alfred-devices'
import { AlfredBulkAddPage } from '@/pages/alfred-bulk-add'
import { AlfredDeviceDetailPage } from '@/pages/alfred-device-detail'
import { AlfredClientDevicesPage } from '@/pages/alfred-client-devices'
import { NotFoundPage } from '@/pages/not-found'
import { requestAlfredTourAutoStart } from '@/lib/alfred-tour'

const App: React.FC = () => {
  const navigate = useNavigate()

  const restoreOriginalUri = async (_oktaAuth: typeof oktaAuth, originalUri: string) => {
    const destination = toRelativeUrl(originalUri || '/alfred/clients', window.location.origin)
    requestAlfredTourAutoStart(new URL(destination, window.location.origin).pathname)
    navigate(destination)
  }

  return (
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
      <div className="min-h-screen bg-background text-foreground">
        <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
          <DatadogUserIdentity />
          <Routes>
            <Route path="/" element={<Navigate to="/alfred/clients" replace />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/login/callback" element={<LoginCallback />} />
            <Route path="/sso" element={<SingleSignOn />} />
            <Route
              element={
                <AlfredBulkImportJobProvider>
                  <SecureRoute />
                </AlfredBulkImportJobProvider>
              }
            >
              <Route path="/go-ahead-pay" element={<Navigate to="/go-ahead-pay/clients" replace />} />
              <Route path="/go-ahead-pay/clients" element={<GapClientsPage />} />
              <Route path="/go-ahead-pay/devices" element={<GapDevicesPage />} />
              <Route path="/alfred" element={<Navigate to="/alfred/clients" replace />} />
              <Route path="/alfred/clients" element={<AlfredClientsPage />} />
              <Route path="/alfred/clients/:id/devices" element={<AlfredClientDevicesPage />} />
              <Route path="/alfred/clients/:id/devices/bulk-add" element={<AlfredBulkAddPage />} />
              <Route path="/alfred/devices" element={<AlfredDevicesPage />} />
              <Route path="/alfred/devices/bulk-add" element={<AlfredBulkAddPage />} />
              <Route path="/alfred/devices/:id" element={<AlfredDeviceDetailPage />} />
              <Route path="/alfred/global" element={<AlfredGlobalPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
          <AlfredTour />
        </Security>
        <Toaster />
      </div>
    </ThemeProvider>
  )
}

export default App
